import { useEffect, useState } from 'react';
import { HelpButton } from '../../components/HelpButton';
import { Pagination } from '../../components';
import { staffAuditLogService, STAFF_PAGE_SIZE } from '../../lib/api-service';
import type { AuditLogEntry } from '../../lib/types';
import { formatDate, formatTime } from '../../utils/date';
import { Loader2, Plus, Pencil, Trash2, Filter } from 'lucide-react';

const ACTION_CONFIG = {
  created: { label: 'Creat', icon: Plus, color: 'text-green-600 bg-green-50' },
  updated: { label: 'Actualizat', icon: Pencil, color: 'text-blue-600 bg-blue-50' },
  deleted: { label: 'Șters', icon: Trash2, color: 'text-red-600 bg-red-50' },
} as const;

export default function AuditLog() {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('');

  useEffect(() => {
    setLoading(true);
    const params: { page?: number; action?: string } = { page };
    if (actionFilter) params.action = actionFilter;
    staffAuditLogService
      .list(params)
      .then((data) => { setEntries(data.results); setTotalCount(data.count); })
      .catch(() => { setEntries([]); setTotalCount(0); })
      .finally(() => setLoading(false));
  }, [actionFilter, page]);

  useEffect(() => { setPage(1); }, [actionFilter]);

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-primary)' }}>
            Jurnal activitate
          </h1>
          <HelpButton to="/staff/documentatie/jurnal" />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="rounded-lg border px-3 py-2 text-sm"
            style={{ fontFamily: 'var(--font-secondary)' }}
          >
            <option value="">Toate acțiunile</option>
            <option value="created">Creat</option>
            <option value="updated">Actualizat</option>
            <option value="deleted">Șters</option>
          </select>
        </div>
      </div>

      {loading && (
        <div className="flex min-h-[30vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-[var(--vivid-purple)]" />
        </div>
      )}

      {!loading && entries.length === 0 && (
        <div className="py-16 text-center text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
          Nicio înregistrare în jurnal.
        </div>
      )}

      {!loading && entries.length > 0 && (
        <div className="overflow-hidden rounded-xl border bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50 text-left" style={{ fontFamily: 'var(--font-primary)' }}>
                <th className="px-4 py-3 font-semibold">Data</th>
                <th className="px-4 py-3 font-semibold">Utilizator</th>
                <th className="px-4 py-3 font-semibold">Activitate</th>
                <th className="px-4 py-3 font-semibold">Acțiune</th>
                <th className="px-4 py-3 font-semibold">Modificări</th>
              </tr>
            </thead>
            <tbody style={{ fontFamily: 'var(--font-secondary)' }}>
              {entries.map((entry) => {
                const config = ACTION_CONFIG[entry.action];
                const Icon = config.icon;
                const changeKeys = Object.keys(entry.changes || {});

                return (
                  <tr key={entry.id} className="border-b last:border-b-0 hover:bg-gray-50">
                    <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                      {formatDate(entry.timestamp)} {formatTime(entry.timestamp)}
                    </td>
                    <td className="px-4 py-3">{entry.user_email || '—'}</td>
                    <td className="px-4 py-3 font-medium">{entry.activity_title}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${config.color}`}>
                        <Icon className="h-3 w-3" />
                        {config.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {changeKeys.length === 0 ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <div className="space-y-1">
                          {changeKeys.map((key) => (
                            <div key={key} className="text-xs">
                              <span className="font-medium">{key}:</span>{' '}
                              <span className="text-red-500 line-through">{entry.changes[key].old || '(gol)'}</span>{' '}
                              → <span className="text-green-600">{entry.changes[key].new || '(gol)'}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && <Pagination page={page} totalCount={totalCount} pageSize={STAFF_PAGE_SIZE} onPageChange={setPage} />}
    </div>
  );
}
